import { useState, useEffect } from 'react'
import { ArrowLeftIcon } from '@heroicons/react/20/solid'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import { useMutation, useLazyQuery } from '@apollo/client'
import { ADD_BOAT, UPDATE_BOAT } from 'graphql/boat/mutations'
import { BoatTypeDropdown } from 'components/custom/BoatTypeDropdown'
import { BoatEquipmentsCheckBox } from 'components/custom/BoatEquipments'
import { BoatAmenitiesCheckBox } from 'components/custom/BoatAmenities'
import { BoatSeatLayoutDropdown } from 'components/custom/BoatSeatLayout'
import { BoatStatusDropdown } from 'components/custom/BoatStatus'
import { ErrorDialog, SuccessDialog } from 'components/custom/ResponseDialog'
import { FileAttachment } from 'components/custom/FileAttachment'
import { SEARCH_BOAT_DETAILS } from 'graphql/boat/queries'

export default function BoatForm() {
    const { id } = useParams()
    const navigate = useNavigate()
    const { t } = useTranslation()
    const [showErrDialog, setShowErrDialog] = useState(false)
    const [showSucDialog, setShowSucDialog] = useState(false)
    const [addBoatError, setAddBoatError] = useState(t(''))
    const [extraLuggageOptions, setExtraLuggageOptions] = useState([])

    // const [boatAttachments, setBoatAttachments] = useState([])
    // const [submitLoading, setSubmitLoading] = useState(false)
    const [boatInfo, setBoatInfo] = useState(null)
    const [formType, setFormType] = useState('add')

    const handleBack = () => {
        // reset the form
        // CreateBoatFormValidation.resetForm();
        navigate('/boats')
    }
    const handleDialogClose = () => {
        setShowSucDialog(false)
        setShowErrDialog(false)
    }

    // Query to get Boat Details by id
    const [getBoatById, { loading: boatListLoading, error: boatListError }] = useLazyQuery(SEARCH_BOAT_DETAILS, {
        fetchPolicy: 'network-only',
        notifyOnNetworkStatusChange: true,
        onCompleted: data => {
            if (data.searchBoat.success && data.searchBoat.code === '200') {
                const boat = data.searchBoat.boats[0]
                setBoatInfo(boat)

                let boatImages = boat.details.images.map(image => {
                    const { __typename, ...rest } = image
                    return rest
                })
                CreateBoatFormValidation.setFieldValue('images', boatImages)

                if (boat.weight_config?.luggage_policy?.extra_luggage_options_kg) {
                    setExtraLuggageOptions(boat.weight_config.luggage_policy.extra_luggage_options_kg)
                }

                setFormType('edit')
            }
        },
    })
    useEffect(() => {
        if (id) {
            searchBoat()
        } else {
            setBoatInfo(null)
            setExtraLuggageOptions([])
            CreateBoatFormValidation.resetForm()
        }
    }, [id])

    useEffect(() => {
        if (boatInfo?.weight_config?.luggage_policy?.extra_luggage_options_kg) {
            setExtraLuggageOptions(boatInfo.weight_config.luggage_policy.extra_luggage_options_kg)
        }
    }, [boatInfo])

    // Search users User List
    const searchBoat = () => {
        getBoatById({
            variables: {
                page: {
                    limit: 1,
                    offset: 0,
                },
                name: '',
                searchBoatId: id,
            },
        })
    }
    // Get Boat Details by id
    const addExtraLuggageOption = () => {
        const newOptions = [...extraLuggageOptions, '']
        setExtraLuggageOptions(newOptions)
        CreateBoatFormValidation.setFieldValue('weight_config.luggage_policy.extra_luggage_options_kg', newOptions)
    }

    const removeExtraLuggageOption = index => {
        if (extraLuggageOptions.length > 1) {
            // Ensure at least one option remains
            const newOptions = extraLuggageOptions.filter((_, i) => i !== index)
            setExtraLuggageOptions(newOptions)
            CreateBoatFormValidation.setFieldValue('weight_config.luggage_policy.extra_luggage_options_kg', newOptions)
        }
    }

    const updateExtraLuggageOption = (index, value) => {
        const newOptions = [...extraLuggageOptions]
        newOptions[index] = parseInt(value) || 0
        setExtraLuggageOptions(newOptions)
        CreateBoatFormValidation.setFieldValue('weight_config.luggage_policy.extra_luggage_options_kg', newOptions)
    }

    // Calculate weight summaries
    const calculateWeightSummaries = () => {
        const values = CreateBoatFormValidation.values
        const weightConfig = values.weight_config
        
        if (!weightConfig) return { maxExtraLuggageTotal: 0, maxPassengerWeight: 0 }
        
        // Get capacity values
        const passengers = parseInt(weightConfig.capacity?.passengers) || 0
        const crew = parseInt(weightConfig.capacity?.crew) || 0
        const captains = parseInt(weightConfig.capacity?.captains) || 0
        
        // Get weight values
        const adultWeight = parseInt(weightConfig.weight_standards?.passengers?.adult_kg) || 0
        const defaultLuggageWeight = parseInt(weightConfig.weight_standards?.default_luggage_kg) || 0
        const maxLoadWeight = parseInt(weightConfig.safety_limits?.max_load_kg) || 0
        
        // Calculate Total Passengers
        const totalPassengers = passengers + crew + captains
        
        // Calculate Max Passenger Weight = Total Passengers × Adult Weight
        const maxPassengerWeight = totalPassengers * adultWeight
        
        // Calculate Max Extra Luggage Total = Max Load Weight − Max Passenger Weight
        const maxExtraLuggageTotal = maxLoadWeight - maxPassengerWeight
        
        // Calculate Max Extra Luggage Total (Final) = Max Extra Luggage Total − (Total Passengers × Default Luggage Weight)
        const maxExtraLuggageTotalFinal = maxExtraLuggageTotal - (totalPassengers * defaultLuggageWeight)
        
        return { 
            maxExtraLuggageTotal: maxExtraLuggageTotalFinal, 
            maxPassengerWeight: maxPassengerWeight,
            totalPassengers: totalPassengers
        }
    }

    //  MUtation Add Boat
    const [addNewBoat, { loading: addLoading }] = useMutation(ADD_BOAT, {
        onCompleted: data => {
            if (data?.addBoat.code === '200' && data?.addBoat.success === true) {
                // setSubmitLoading(false)
                setShowSucDialog(true)
                // Time delay to redirect to the users page
                setTimeout(() => {
                    handleBack()
                }, 500)
            } else {
                // setSubmitLoading(false)
                setAddBoatError(data?.addBoat.message)
                setShowErrDialog(true)
            }
        },
        onError: error => {
            // setSubmitLoading(false)
            setAddBoatError(error.message)
            setShowErrDialog(true)
        },
    })
    // Mutation Update Boat
    const [updateBoat, { loading: updateLoading }] = useMutation(UPDATE_BOAT, {
        onCompleted: data => {
            if (data?.updateBoat.code === '200' && data?.updateBoat.success === true) {
                // setSubmitLoading(false)
                setShowSucDialog(true)
                // Time delay to redirect to the users page
                setTimeout(() => {
                    handleBack()
                }, 500)
            } else {
                // setSubmitLoading(false)
                setAddBoatError(data?.updateBoat.message)
                setShowErrDialog(true)
            }
        },
        onError: error => {
            // setSubmitLoading(false)
            setAddBoatError(error.message)
            setShowErrDialog(true)
        },
    })

    // Form Validation
    const CreateBoatFormValidation = useFormik({
        enableReinitialize: true,
        initialValues: {
            boat_name: boatInfo?.name?.en || '',
            boat_type: boatInfo?.details?.boat_type.code || '',
            reg_number: boatInfo?.details?.reg_number || '',
            capacity: boatInfo?.details?.capacity || '',
            amenities: boatInfo?.details?.features_amenities?.map(amenity => amenity.code) || [],
            safety_equipments: boatInfo?.details?.safety_equipment?.map(equipment => equipment.code) || [],
            description: boatInfo?.details?.description?.en || '',
            images: boatInfo?.details?.images || [],
            seat_layout: boatInfo?.seat_layout?.layout_id || '',
            status: boatInfo?.status.code || '',
            weight_config: {
                capacity: {
                    passengers: boatInfo?.weight_config?.capacity?.passengers || '',
                    crew: boatInfo?.weight_config?.capacity?.crew || '',
                    captains: boatInfo?.weight_config?.capacity?.captains || '',
                },
                weight_standards: {
                    passengers: {
                        infant_kg: boatInfo?.weight_config?.weight_standards?.passengers?.infant_kg || '',
                        child_kg: boatInfo?.weight_config?.weight_standards?.passengers?.child_kg || '',
                        adult_kg: boatInfo?.weight_config?.weight_standards?.passengers?.adult_kg || '',
                        senior_kg: boatInfo?.weight_config?.weight_standards?.passengers?.senior_kg || '',
                    },
                    default_luggage_kg: boatInfo?.weight_config?.weight_standards?.default_luggage_kg || '',
                },
                luggage_policy: {
                    extra_luggage_options_kg: boatInfo?.weight_config?.luggage_policy?.extra_luggage_options_kg || [
                        3, 5, 10,
                    ],
                },
                safety_limits: {
                    max_load_kg: boatInfo?.weight_config?.safety_limits?.max_load_kg || '',
                },
                special_items_limits: {
                    strollers: boatInfo?.weight_config?.special_items_limits?.strollers || '',
                    wheelchairs: boatInfo?.weight_config?.special_items_limits?.wheelchairs || '',
                },
            },
        },
        validationSchema: Yup.object().shape({
            boat_name: Yup.string()
                .max(50, t('Boat Name must be at most 50 characters'))
                .required(t('Boat Name is required')),
            boat_type: Yup.string().required(t('Boat Type is required')),
            reg_number: Yup.string()
                .max(50, t('Registration must be at most 50 characters'))
                .required(t('Registration number is required')),
            capacity: Yup.number()
                .positive(t('Capacity must be a positive number'))
                .min(1, t('Capacity must be greater than 0'))
                .required(t('Capacity is required')),
            amenities: Yup.array()
                .of(Yup.string().required(t('Amenities is required')))
                .min(1, t('At least one amenities required')),
            safety_equipments: Yup.array()
                .of(Yup.string().required(t('Safety Equipments is required')))
                .min(1, t('At least one safety equipments required')),
            description: Yup.string().required(t('Description is required')),
            images: Yup.array().of(Yup.object().shape({})).min(1, t('At least one image required')),
            seat_layout: Yup.string().required(t('Seat Layout is required')),
            status: Yup.string().required(t('Status is required')),
            weight_config: Yup.object()
                .shape({
                    capacity: Yup.object().shape({
                        passengers: Yup.number().positive().required(t('Passengers capacity is required')),
                        crew: Yup.number().positive().required(t('Crew capacity is required')),
                        captains: Yup.number().positive().required(t('Captains capacity is required')),
                    }),
                    weight_standards: Yup.object().shape({
                        passengers: Yup.object().shape({
                            infant_kg: Yup.number().positive().required(t('Infant weight is required')),
                            child_kg: Yup.number().positive().required(t('Child weight is required')),
                            adult_kg: Yup.number().positive().required(t('Adult weight is required')),
                            senior_kg: Yup.number().positive().required(t('Senior weight is required')),
                        }),
                        default_luggage_kg: Yup.number().positive().required(t('Default luggage weight is required')),
                    }),
                    luggage_policy: Yup.object().shape({
                        extra_luggage_options_kg: Yup.array()
                            .of(Yup.number().positive())
                            .min(1, t('At least one extra luggage option required')),
                    }),
                    safety_limits: Yup.object().shape({
                        max_load_kg: Yup.number().positive().required(t('Max load weight is required')),
                    }),
                    special_items_limits: Yup.object().shape({
                        strollers: Yup.number().min(0).required(t('Strollers limit is required')),
                        wheelchairs: Yup.number().min(0).required(t('Wheelchairs limit is required')),
                    }),
                })
                .required(t('Weight configuration is required')),
        }),
        onSubmit: values => {
            // setSubmitLoading(true);
            submitBoat(values)
        },
    })

    const submitBoat = async boatInfo => {
        CreateBoatFormValidation.setSubmitting(true)
        // remove  "file_data": "File" from the images array
        let boatImages = boatInfo.images.map(image => {
            const { file_data, ...rest } = image
            return rest
        })
        const formattedData = {
            boat: {
                name: {
                    en: boatInfo.boat_name,
                    ar: boatInfo.boat_name,
                },
                details: {
                    reg_number: boatInfo.reg_number,
                    boat_type_code: boatInfo.boat_type,
                    capacity: boatInfo.capacity,
                    safety_equipment: boatInfo.safety_equipments,
                    description: {
                        en: boatInfo.description,
                        ar: boatInfo.description,
                    },
                    features_amenities: boatInfo.amenities,
                    images: boatImages.map(image => {
                        return {
                            file_name: image.file_name,
                            size: image.size,
                            mime_type: image.mime_type,
                            s3_key: image.s3_key,
                        }
                    }),
                },
                status_code: boatInfo.status,
                seat_layout_id: boatInfo.seat_layout,
                weight_config: {
                    capacity: {
                        passengers: parseInt(boatInfo.weight_config.capacity.passengers),
                        crew: parseInt(boatInfo.weight_config.capacity.crew),
                        captains: parseInt(boatInfo.weight_config.capacity.captains),
                    },
                    weight_standards: {
                        passengers: {
                            infant_kg: parseInt(boatInfo.weight_config.weight_standards.passengers.infant_kg),
                            child_kg: parseInt(boatInfo.weight_config.weight_standards.passengers.child_kg),
                            adult_kg: parseInt(boatInfo.weight_config.weight_standards.passengers.adult_kg),
                            senior_kg: parseInt(boatInfo.weight_config.weight_standards.passengers.senior_kg),
                        },
                        default_luggage_kg: parseInt(boatInfo.weight_config.weight_standards.default_luggage_kg),
                    },
                    luggage_policy: {
                        extra_luggage_options_kg: boatInfo.weight_config.luggage_policy.extra_luggage_options_kg,
                        max_extra_luggage_total_kg: calculateWeightSummaries().maxExtraLuggageTotal,
                    },
                    safety_limits: {
                        max_load_kg: parseInt(boatInfo.weight_config.safety_limits.max_load_kg),
                        max_passenger_weight_kg: calculateWeightSummaries().maxPassengerWeight,
                    },
                    special_items_limits: {
                        strollers: parseInt(boatInfo.weight_config.special_items_limits.strollers),
                        wheelchairs: parseInt(boatInfo.weight_config.special_items_limits.wheelchairs),
                    },
                },
            },
        }

        if (id) {
            formattedData['boatId'] = id
            updateBoat({
                variables: formattedData,
            })
        } else {
            addNewBoat({
                variables: formattedData,
            })
        }
    }

    return (
        <>
            <div>
                <div className="mb-8 flex justify-between items-center">
                    <button
                        onClick={handleBack}
                        type="button"
                        className="inline-flex items-center gap-x-0.5 rounded-md text-sm text-gray-600"
                    >
                        <ArrowLeftIcon aria-hidden="true" className="-ml-0.5 h-5 w-4 rtl:rotate-180" />
                        {t('Back')}
                    </button>
                    <div className="flex gap-3">
                        <button
                            onClick={handleBack}
                            type="button"
                            className="inline-flex items-center justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                        >
                            {t('Cancel')}
                        </button>
                        <button
                            disabled={CreateBoatFormValidation.isSubmitting}
                            onClick={CreateBoatFormValidation.handleSubmit}
                            type="button"
                            className="hover:bg-primary/[.9] bg-primary focus-visible:outline-primary justify-center rounded-md px-3 py-1.5 text-sm font-semibold leading-6 text-white shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
                        >
                            {t('Save')}
                        </button>
                    </div>
                </div>
                <form>
                    <div className="space-y-12">
                        <div className="grid grid-cols-1 gap-x-8 gap-y-10 border-b border-gray-900/10 pb-12 md:grid-cols-3">
                            <div>
                                <h2 className="text-base font-semibold leading-7 text-gray-900">
                                    {t('Boat Information')}
                                </h2>
                                <p className="mt-1 text-sm leading-6 text-gray-600">
                                    {t('Provide the details of the boat.')}
                                </p>
                            </div>

                            <div className="grid max-w-2xl grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-6 md:col-span-2">
                                <div className="sm:col-span-full">
                                    <label
                                        htmlFor="boat-name"
                                        className="block text-sm font-medium leading-6 text-gray-900"
                                    >
                                        {t('Boat Name')}
                                        <span className="text-red">*</span>
                                    </label>
                                    <input
                                        id="boat_name"
                                        name="boat_name"
                                        type="text"
                                        value={CreateBoatFormValidation.values.boat_name}
                                        placeholder="Enter here"
                                        onChange={CreateBoatFormValidation.handleChange}
                                        onBlur={CreateBoatFormValidation.handleBlur}
                                        className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary sm:text-sm sm:leading-6"
                                    />
                                    {CreateBoatFormValidation.touched.boat_name &&
                                        CreateBoatFormValidation.errors.boat_name && (
                                            <p className="text-error text-xs mt-1">
                                                {CreateBoatFormValidation.errors.boat_name}
                                            </p>
                                        )}
                                </div>

                                <div className="sm:col-span-full">
                                    <label
                                        htmlFor="boat-type"
                                        className="block text-sm font-medium leading-6 text-gray-900"
                                    >
                                        {t('Boat Type')}
                                        <span className="text-red">*</span>
                                    </label>
                                    <BoatTypeDropdown
                                        value={CreateBoatFormValidation.values.boat_type}
                                        onChange={CreateBoatFormValidation.handleChange}
                                        onBlur={CreateBoatFormValidation.handleBlur}
                                        error={
                                            CreateBoatFormValidation.touched.boat_type &&
                                            !!CreateBoatFormValidation.errors.boat_type
                                        }
                                        helperText={
                                            CreateBoatFormValidation.touched.boat_type
                                                ? CreateBoatFormValidation.errors.boat_type
                                                : ''
                                        }
                                    />
                                </div>

                                <div className="sm:col-span-full">
                                    <label
                                        htmlFor="boat-name"
                                        className="block text-sm font-medium leading-6 text-gray-900"
                                    >
                                        {t('Registration No')}
                                        <span className="text-red">*</span>
                                    </label>
                                    <input
                                        id="reg_number"
                                        name="reg_number"
                                        type="text"
                                        value={CreateBoatFormValidation.values.reg_number}
                                        placeholder="Enter here"
                                        onChange={CreateBoatFormValidation.handleChange}
                                        onBlur={CreateBoatFormValidation.handleBlur}
                                        className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary sm:text-sm sm:leading-6"
                                    />
                                    {CreateBoatFormValidation.touched.reg_number &&
                                        CreateBoatFormValidation.errors.reg_number && (
                                            <p className="text-error text-xs mt-1">
                                                {CreateBoatFormValidation.errors.reg_number}
                                            </p>
                                        )}
                                </div>

                                <div className="sm:col-span-full">
                                    <label
                                        htmlFor="capacity"
                                        className="block text-sm font-medium leading-6 text-gray-900"
                                    >
                                        {t('Capacity')}
                                        <span className="text-red">*</span>
                                    </label>
                                    <input
                                        id="capacity"
                                        name="capacity"
                                        type="number"
                                        value={CreateBoatFormValidation.values.capacity}
                                        placeholder="Enter here"
                                        onChange={CreateBoatFormValidation.handleChange}
                                        onBlur={CreateBoatFormValidation.handleBlur}
                                        className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary sm:text-sm sm:leading-6"
                                    />
                                    {CreateBoatFormValidation.touched.capacity &&
                                        CreateBoatFormValidation.errors.capacity && (
                                            <p className="text-error text-xs mt-1">
                                                {CreateBoatFormValidation.errors.capacity}
                                            </p>
                                        )}
                                </div>

                                <div className="sm:col-span-full">
                                    <label className="block text-sm font-medium leading-6 text-gray-900">
                                        {t('Features and Amenities')}
                                        <span className="text-red">*</span>
                                    </label>
                                    <div className="mt-2 space-y-2">
                                        <BoatAmenitiesCheckBox
                                            value={CreateBoatFormValidation.values.amenities}
                                            onChange={amenities =>
                                                CreateBoatFormValidation.setFieldValue('amenities', amenities)
                                            }
                                            error={
                                                CreateBoatFormValidation.touched.amenities &&
                                                !!CreateBoatFormValidation.errors.amenities
                                            }
                                            helperText={
                                                CreateBoatFormValidation.touched.amenities
                                                    ? CreateBoatFormValidation.errors.amenities
                                                    : ''
                                            }
                                        />
                                    </div>
                                </div>

                                <div className="sm:col-span-full">
                                    <label className="block text-sm font-medium leading-6 text-gray-900">
                                        {t('Safety Equipments')}
                                        <span className="text-red">*</span>
                                    </label>
                                    <div className="mt-2 space-y-2">
                                        <BoatEquipmentsCheckBox
                                            value={CreateBoatFormValidation.values.safety_equipments}
                                            onChange={equipments =>
                                                CreateBoatFormValidation.setFieldValue('safety_equipments', equipments)
                                            }
                                            error={
                                                CreateBoatFormValidation.touched.safety_equipments &&
                                                !!CreateBoatFormValidation.errors.safety_equipments
                                            }
                                            helperText={
                                                CreateBoatFormValidation.touched.safety_equipments
                                                    ? CreateBoatFormValidation.errors.safety_equipments
                                                    : ''
                                            }
                                        />
                                    </div>
                                </div>

                                <div className="sm:col-span-full">
                                    <label
                                        htmlFor="description"
                                        className="block text-sm font-medium leading-6 text-gray-900"
                                    >
                                        {t('Boat Description')}
                                        <span className="text-red">*</span>
                                    </label>
                                    <textarea
                                        id="description"
                                        name="description"
                                        rows="4"
                                        value={CreateBoatFormValidation.values.description}
                                        placeholder="Enter here"
                                        onChange={CreateBoatFormValidation.handleChange}
                                        onBlur={CreateBoatFormValidation.handleBlur}
                                        className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary sm:text-sm sm:leading-6"
                                    />
                                    {CreateBoatFormValidation.touched.description &&
                                        CreateBoatFormValidation.errors.description && (
                                            <p className="text-error text-xs mt-1">
                                                {CreateBoatFormValidation.errors.description}
                                            </p>
                                        )}
                                </div>

                                {/* <div>
                                    <label
                                        htmlFor="boat-images"
                                        className="block text-sm font-medium leading-6 text-gray-900"
                                    >
                                        {t('Boat Images')}
                                        <span className="text-red">*</span>
                                    </label>
                                    <input
                                        id="boat-images"
                                        name="boat-images"
                                        type="file"
                                        multiple
                                        onChange={handleFileChange}
                                        className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-primary sm:text-sm sm:leading-6"
                                    />
                                </div> */}

                                <FileAttachment
                                    isRequired={true}
                                    maxLength={5}
                                    uploadLocation="public/boat"
                                    title={t('Boat Images')}
                                    onChange={files => CreateBoatFormValidation.setFieldValue('images', files)}
                                    attachments={CreateBoatFormValidation.values.images}
                                    //fileSupported={['image/jpeg', 'image/png', 'image/jpg']}
                                    maxSize={5}
                                ></FileAttachment>
                                {CreateBoatFormValidation.touched.images && CreateBoatFormValidation.errors.images && (
                                    <p className="text-error text-xs mt-1">{CreateBoatFormValidation.errors.images}</p>
                                )}

                                <div className="sm:col-span-full">
                                    <label
                                        htmlFor="seat-layout"
                                        className="block text-sm font-medium leading-6 text-gray-900"
                                    >
                                        {t('Seat Layout')}
                                        <span className="text-red">*</span>
                                    </label>
                                    <BoatSeatLayoutDropdown
                                        value={CreateBoatFormValidation.values.seat_layout}
                                        onChange={CreateBoatFormValidation.handleChange}
                                        onBlur={CreateBoatFormValidation.handleBlur}
                                        error={
                                            CreateBoatFormValidation.touched.seat_layout &&
                                            !!CreateBoatFormValidation.errors.seat_layout
                                        }
                                        helperText={
                                            CreateBoatFormValidation.touched.seat_layout
                                                ? CreateBoatFormValidation.errors.seat_layout
                                                : ''
                                        }
                                    />
                                </div>

                                <div className="sm:col-span-full">
                                    <label
                                        htmlFor="status"
                                        className="block text-sm font-medium leading-6 text-gray-900"
                                    >
                                        {t('Status')}
                                        <span className="text-red">*</span>
                                    </label>
                                    <BoatStatusDropdown
                                        value={CreateBoatFormValidation.values.status}
                                        onChange={CreateBoatFormValidation.handleChange}
                                        onBlur={CreateBoatFormValidation.handleBlur}
                                        error={
                                            CreateBoatFormValidation.touched.status &&
                                            !!CreateBoatFormValidation.errors.status
                                        }
                                        helperText={
                                            CreateBoatFormValidation.touched.status
                                                ? CreateBoatFormValidation.errors.status
                                                : ''
                                        }
                                        purpose="add"
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Luggage Configuration Section */}
                        <div className="grid grid-cols-1 gap-x-8 gap-y-10 border-b border-gray-900/10 pb-12 md:grid-cols-3">
                            <div>
                                <h2 className="text-base font-semibold leading-7 text-gray-900">
                                    {t('Luggage Configuration')}
                                </h2>
                                <p className="mt-1 text-sm leading-6 text-gray-600">
                                    {t('Configure weight and luggage policies for international trips.')}
                                </p>
                            </div>

                            <div className="grid max-w-2xl grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-6 md:col-span-2">
                                {/* Capacity Configuration */}
                                <div className="sm:col-span-full">
                                    <h3 className="text-sm font-semibold leading-6 text-gray-900 mb-4">
                                        {t('Capacity Configuration')}
                                    </h3>
                                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                                        <div>
                                            <label className="block text-sm font-medium leading-6 text-gray-900">
                                                {t('Passengers')}
                                                <span className="text-red">*</span>
                                            </label>
                                            <input
                                                type="number"
                                                name="weight_config.capacity.passengers"
                                                value={
                                                    CreateBoatFormValidation.values.weight_config?.capacity
                                                        ?.passengers || ''
                                                }
                                                onChange={CreateBoatFormValidation.handleChange}
                                                onBlur={CreateBoatFormValidation.handleBlur}
                                                className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary sm:text-sm sm:leading-6"
                                                placeholder="0"
                                            />
                                            {CreateBoatFormValidation.touched.weight_config?.capacity?.passengers &&
                                                CreateBoatFormValidation.errors.weight_config?.capacity?.passengers && (
                                                    <p className="text-error text-xs mt-1">
                                                        {
                                                            CreateBoatFormValidation.errors.weight_config.capacity
                                                                .passengers
                                                        }
                                                    </p>
                                                )}
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium leading-6 text-gray-900">
                                                {t('Crew')}
                                                <span className="text-red">*</span>
                                            </label>
                                            <input
                                                type="number"
                                                name="weight_config.capacity.crew"
                                                value={
                                                    CreateBoatFormValidation.values.weight_config?.capacity?.crew || ''
                                                }
                                                onChange={CreateBoatFormValidation.handleChange}
                                                onBlur={CreateBoatFormValidation.handleBlur}
                                                className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary sm:text-sm sm:leading-6"
                                                placeholder="0"
                                            />
                                            {CreateBoatFormValidation.touched.weight_config?.capacity?.crew &&
                                                CreateBoatFormValidation.errors.weight_config?.capacity?.crew && (
                                                    <p className="text-error text-xs mt-1">
                                                        {CreateBoatFormValidation.errors.weight_config.capacity.crew}
                                                    </p>
                                                )}
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium leading-6 text-gray-900">
                                                {t('Captains')}
                                                <span className="text-red">*</span>
                                            </label>
                                            <input
                                                type="number"
                                                name="weight_config.capacity.captains"
                                                value={
                                                    CreateBoatFormValidation.values.weight_config?.capacity?.captains ||
                                                    ''
                                                }
                                                onChange={CreateBoatFormValidation.handleChange}
                                                onBlur={CreateBoatFormValidation.handleBlur}
                                                className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary sm:text-sm sm:leading-6"
                                                placeholder="0"
                                            />
                                            {CreateBoatFormValidation.touched.weight_config?.capacity?.captains &&
                                                CreateBoatFormValidation.errors.weight_config?.capacity?.captains && (
                                                    <p className="text-error text-xs mt-1">
                                                        {
                                                            CreateBoatFormValidation.errors.weight_config.capacity
                                                                .captains
                                                        }
                                                    </p>
                                                )}
                                        </div>
                                    </div>
                                </div>

                                {/* Weight Standards */}
                                <div className="sm:col-span-full">
                                    <h3 className="text-sm font-semibold leading-6 text-gray-900 mb-4">
                                        {t('Weight Standards (kg)')}
                                    </h3>
                                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                                        <div>
                                            <label className="block text-sm font-medium leading-6 text-gray-900">
                                                {t('Infant')}
                                                <span className="text-red">*</span>
                                            </label>
                                            <input
                                                type="number"
                                                name="weight_config.weight_standards.passengers.infant_kg"
                                                value={
                                                    CreateBoatFormValidation.values.weight_config?.weight_standards
                                                        ?.passengers?.infant_kg || ''
                                                }
                                                onChange={CreateBoatFormValidation.handleChange}
                                                onBlur={CreateBoatFormValidation.handleBlur}
                                                className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary sm:text-sm sm:leading-6"
                                                placeholder="10"
                                            />
                                            {CreateBoatFormValidation.touched.weight_config?.weight_standards
                                                ?.passengers?.infant_kg &&
                                                CreateBoatFormValidation.errors.weight_config?.weight_standards
                                                    ?.passengers?.infant_kg && (
                                                    <p className="text-error text-xs mt-1">
                                                        {
                                                            CreateBoatFormValidation.errors.weight_config
                                                                .weight_standards.passengers.infant_kg
                                                        }
                                                    </p>
                                                )}
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium leading-6 text-gray-900">
                                                {t('Child')}
                                                <span className="text-red">*</span>
                                            </label>
                                            <input
                                                type="number"
                                                name="weight_config.weight_standards.passengers.child_kg"
                                                value={
                                                    CreateBoatFormValidation.values.weight_config?.weight_standards
                                                        ?.passengers?.child_kg || ''
                                                }
                                                onChange={CreateBoatFormValidation.handleChange}
                                                onBlur={CreateBoatFormValidation.handleBlur}
                                                className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary sm:text-sm sm:leading-6"
                                                placeholder="40"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium leading-6 text-gray-900">
                                                {t('Adult')}
                                                <span className="text-red">*</span>
                                            </label>
                                            <input
                                                type="number"
                                                name="weight_config.weight_standards.passengers.adult_kg"
                                                value={
                                                    CreateBoatFormValidation.values.weight_config?.weight_standards
                                                        ?.passengers?.adult_kg || ''
                                                }
                                                onChange={CreateBoatFormValidation.handleChange}
                                                onBlur={CreateBoatFormValidation.handleBlur}
                                                className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary sm:text-sm sm:leading-6"
                                                placeholder="75"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium leading-6 text-gray-900">
                                                {t('Senior')}
                                                <span className="text-red">*</span>
                                            </label>
                                            <input
                                                type="number"
                                                name="weight_config.weight_standards.passengers.senior_kg"
                                                value={
                                                    CreateBoatFormValidation.values.weight_config?.weight_standards
                                                        ?.passengers?.senior_kg || ''
                                                }
                                                onChange={CreateBoatFormValidation.handleChange}
                                                onBlur={CreateBoatFormValidation.handleBlur}
                                                className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary sm:text-sm sm:leading-6"
                                                placeholder="75"
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* Default Luggage Weight */}
                                <div className="sm:col-span-full">
                                    <label className="block text-sm font-medium leading-6 text-gray-900">
                                        {t('Default Luggage Weight (kg)')}
                                        <span className="text-red">*</span>
                                    </label>
                                    <input
                                        type="number"
                                        name="weight_config.weight_standards.default_luggage_kg"
                                        value={
                                            CreateBoatFormValidation.values.weight_config?.weight_standards
                                                ?.default_luggage_kg || ''
                                        }
                                        onChange={CreateBoatFormValidation.handleChange}
                                        onBlur={CreateBoatFormValidation.handleBlur}
                                        className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary sm:text-sm sm:leading-6"
                                        placeholder="10"
                                    />
                                </div>

                                {/* Luggage Policy */}
                                {/* <div className="sm:col-span-full">
                                    <h3 className="text-sm font-semibold leading-6 text-gray-900 mb-4">
                                        {t('Luggage Policy')}
                                    </h3> */}

                                    {/* Extra Luggage Options */}
                                    {/* <div className="mb-4">
                                        <label className="block text-sm font-medium leading-6 text-gray-900 mb-2">
                                            {t('Extra Luggage Options (kg)')}
                                            <span className="text-red">*</span>
                                        </label>
                                        <div className="space-y-2">
                                            {extraLuggageOptions.map((option, index) => (
                                                <div key={index} className="flex items-center gap-2">
                                                    <input
                                                        type="number"
                                                        value={option}
                                                        onChange={e => updateExtraLuggageOption(index, e.target.value)}
                                                        className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary sm:text-sm sm:leading-6"
                                                        placeholder="Weight in kg"
                                                    />
                                                    {extraLuggageOptions.length > 1 && (
                                                        <button
                                                            type="button"
                                                            onClick={() => removeExtraLuggageOption(index)}
                                                            className="px-3 py-1.5 text-sm text-red-600 hover:text-red-800"
                                                        >
                                                            {t('Remove')}
                                                        </button>
                                                    )}
                                                </div>
                                            ))}
                                            <button
                                                type="button"
                                                onClick={addExtraLuggageOption}
                                                className="mt-2 inline-flex items-center px-3 py-2 text-sm font-semibold text-primary hover:text-primary/80"
                                            >
                                                + {t('Add Option')}
                                            </button>
                                        </div>
                                    </div> */}

                                {/* </div> */}

                                {/* Safety Limits */}
                                <div className="sm:col-span-full">
                                    <h3 className="text-sm font-semibold leading-6 text-gray-900 mb-4">
                                        {t('Safety Limits (kg)')}
                                    </h3>
                                    <div>
                                        <label className="block text-sm font-medium leading-6 text-gray-900">
                                            {t('Max Load Weight')}
                                            <span className="text-red">*</span>
                                        </label>
                                        <input
                                            type="number"
                                            name="weight_config.safety_limits.max_load_kg"
                                            value={
                                                CreateBoatFormValidation.values.weight_config?.safety_limits
                                                    ?.max_load_kg || ''
                                            }
                                            onChange={CreateBoatFormValidation.handleChange}
                                            onBlur={CreateBoatFormValidation.handleBlur}
                                            className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary sm:text-sm sm:leading-6"
                                            placeholder="1500"
                                        />
                                    </div>
                                </div>

                                {/* Special Items Limits */}
                                <div className="sm:col-span-full">
                                    <h3 className="text-sm font-semibold leading-6 text-gray-900 mb-4">
                                        {t('Special Items Limits')}
                                    </h3>
                                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                                        <div>
                                            <label className="block text-sm font-medium leading-6 text-gray-900">
                                                {t('Strollers')}
                                                <span className="text-red">*</span>
                                            </label>
                                            <input
                                                type="number"
                                                name="weight_config.special_items_limits.strollers"
                                                value={
                                                    CreateBoatFormValidation.values.weight_config?.special_items_limits
                                                        ?.strollers || ''
                                                }
                                                onChange={CreateBoatFormValidation.handleChange}
                                                onBlur={CreateBoatFormValidation.handleBlur}
                                                className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary sm:text-sm sm:leading-6"
                                                placeholder="2"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium leading-6 text-gray-900">
                                                {t('Wheelchairs')}
                                                <span className="text-red">*</span>
                                            </label>
                                            <input
                                                type="number"
                                                name="weight_config.special_items_limits.wheelchairs"
                                                value={
                                                    CreateBoatFormValidation.values.weight_config?.special_items_limits
                                                        ?.wheelchairs || ''
                                                }
                                                onChange={CreateBoatFormValidation.handleChange}
                                                onBlur={CreateBoatFormValidation.handleBlur}
                                                className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary sm:text-sm sm:leading-6"
                                                placeholder="1"
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* Weight Summary Display */}
                                <div className="sm:col-span-full">
                                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                                        <h3 className="text-sm font-semibold leading-6 text-blue-900 mb-3">
                                            {t('Weight Summary')}
                                        </h3>
                                        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                                            <div className="bg-white rounded-md p-3 border border-blue-100">
                                                <div className="text-sm font-medium text-blue-700">
                                                    {t('Calculated Max Extra Luggage Total (kg)')}
                                                </div>
                                                <div className="text-lg font-bold text-blue-900">
                                                    {calculateWeightSummaries().maxExtraLuggageTotal} kg
                                                </div>
                                                <div className="text-xs text-blue-600 mt-1">
                                                    {t('Max Load Weight - Max Passenger Weight - (Total Passengers × Default Luggage Weight)')}
                                                </div>
                                            </div>
                                            <div className="bg-white rounded-md p-3 border border-blue-100">
                                                <div className="text-sm font-medium text-blue-700">
                                                    {t('Calculated Max Passenger Weight (kg)')}
                                                </div>
                                                <div className="text-lg font-bold text-blue-900">
                                                    {calculateWeightSummaries().maxPassengerWeight} kg
                                                </div>
                                                <div className="text-xs text-blue-600 mt-1">
                                                    {t('Total Passengers × Adult Weight')}
                                                </div>
                                            </div>
                                        </div>
                                        <div className="mt-3 bg-white rounded-md p-3 border border-blue-100">
                                            <div className="text-sm font-medium text-blue-700 mb-2">
                                                {t('Calculation Breakdown')}
                                            </div>
                                            <div className="text-xs text-blue-600 space-y-1">
                                                <div>{t('Total Passengers')}: {calculateWeightSummaries().totalPassengers} ({t('Passengers')} + {t('Crew')} + {t('Captains')})</div>
                                                <div>{t('Max Passenger Weight')}: {calculateWeightSummaries().totalPassengers} × {parseInt(CreateBoatFormValidation.values.weight_config?.weight_standards?.passengers?.adult_kg) || 0} = {calculateWeightSummaries().maxPassengerWeight} kg</div>
                                                <div>{t('Max Extra Luggage Total')}: {parseInt(CreateBoatFormValidation.values.weight_config?.safety_limits?.max_load_kg) || 0} - {calculateWeightSummaries().maxPassengerWeight} = {(parseInt(CreateBoatFormValidation.values.weight_config?.safety_limits?.max_load_kg) || 0) - calculateWeightSummaries().maxPassengerWeight} kg</div>
                                                <div>{t('Final Max Extra Luggage Total')}: {(parseInt(CreateBoatFormValidation.values.weight_config?.safety_limits?.max_load_kg) || 0) - calculateWeightSummaries().maxPassengerWeight} - ({calculateWeightSummaries().totalPassengers} × {parseInt(CreateBoatFormValidation.values.weight_config?.weight_standards?.default_luggage_kg) || 0}) = {calculateWeightSummaries().maxExtraLuggageTotal} kg</div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </form>
                <div className="mt-10 mb-8 flex justify-between items-center">
                    {/* <a href="/boats" className="inline-flex items-center gap-x-1.5 rounded-md text-sm">
                        <ArrowLeftIcon aria-hidden="true" className="-ml-0.5 h-5 w-5" />
                        Back
                    </a> */}
                    <p></p>
                    <div className="flex gap-3">
                        <button
                            onClick={handleBack}
                            type="button"
                            className="inline-flex items-center justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                        >
                            {t('Cancel')}
                        </button>
                        <button
                            onClick={CreateBoatFormValidation.handleSubmit}
                            disabled={formType === 'add' ? addLoading : updateLoading}
                            type="button"
                            className="hover:bg-primary/[.9] bg-primary focus-visible:outline-primary justify-center rounded-md px-3 py-1.5 text-sm font-semibold leading-6 text-white shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
                        >
                            {t('Save')}
                        </button>
                    </div>
                </div>
            </div>
            {showErrDialog && (
                <ErrorDialog status={true} title="Error" message={addBoatError} onClose={handleDialogClose} />
            )}
            {showSucDialog && (
                <SuccessDialog
                    status={true}
                    title={t('Success')}
                    message={t('Saved Successfully')}
                    onClose={handleDialogClose}
                />
            )}
            {/* Overlay and Loading Spinner */}
            {(formType === 'add' ? addLoading : updateLoading) && (
                <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
                    <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-white"></div>
                </div>
            )}
        </>
    )
}
